// @ts-check
import React, {
  createRef,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from "react"
import { CSSTransition } from "react-transition-group"
import { classNames } from "primereact/utils"
import { Ripple } from "primereact/ripple"
import useSidebarContext from "../../hooks/contextConsumers/useSidebarContext.hook"
import useScreenDimensionsContext from "../../hooks/contextConsumers/useScreenDimensionsContext.hook"
import IconMaterial from "../../components/atoms/IconMaterial.atom"
import { useNavigate } from "react-router-dom"
import BLink from "../../components/atoms/Blink"

/**
 * @typedef {import("../../types/appMenu").RenderableAppMenuItem} RenderableAppMenuItem
 * @typedef {import("../../types/appMenu").RenderableExpandableAppMenuItem} RenderableExpandableAppMenuItem
 * @typedef {import("../../types/appMenu").RenderableNavigableAppMenuItem} RenderableNavigableAppMenuItem
 * @typedef {import("../../types/appMenu").RenderableOverlayAppMenuItem} RenderableOverlayAppMenuItem
 */

/**
 * @typedef {Object} AppSubmenuProps
 * @property {RenderableAppMenuItem[]} items
 * @property {string} menuMode
 * @property {boolean} menuActive
 * @property {boolean} parentMenuItemActive
 * @property {boolean} [root]
 * @property {(event: React.MouseEvent, item: RenderableOverlayAppMenuItem) => void} onOverlayMenuItemClick
 * @property {string} [className]
 * @property {boolean} [resetActiveIndex]
 */

export const AppSubmenu = forwardRef(
  /**
   * @param {AppSubmenuProps} props
   * @param {*} ref
   * @returns
   */
  (props, ref) => {
    const { keyPath, setKeyPath, setScreenTitle, setSidebarActive } =
      useSidebarContext()

    const { setShowFiltersMenu, setHideMobileMenu } =
      useScreenDimensionsContext()

    /** @type {[number|null, function]} */
    const [activeIndex, setActiveIndex] = useState(null)
    const navigate = useNavigate()

    useEffect(() => {
      if (!keyPath || !props?.items) return

      const index = props?.items.findIndex((item) =>
        checkSectionIfEqualsToPath(keyPath, item)
      )

      setActiveIndex(index !== -1 ? index : null)
    }, [props?.items, keyPath])

    /**
     * @param {string} keyPath
     * @param {RenderableAppMenuItem} item
     * @returns {boolean}
     */
    function checkSectionIfEqualsToPath(keyPath, item) {
      if (keyPath === item.path) return true
      if (!isExpandable(item)) return false

      return item.modules.some((itemModule) => itemModule.path === keyPath)
    }

    /**
     * @param {RenderableAppMenuItem} item
     * @returns {item is RenderableExpandableAppMenuItem}
     */
    function isExpandable(item) {
      return Array.isArray(item.modules)
    }

    /**
     * @param {RenderableAppMenuItem} item
     * @returns {item is RenderableNavigableAppMenuItem}
     */
    function isNavigable(item) {
      return typeof item.path === "string"
    }

    /**
     * @param {RenderableAppMenuItem} item
     * @returns {item is RenderableOverlayAppMenuItem}
     */
    function isOverlay(item) {
      return typeof item.overlay === "string"
    }

    /**
     * @param {React.MouseEvent} event
     * @param {RenderableAppMenuItem} item
     * @param {number} index
     */
    function onMenuItemClick(event, item, index) {
      event.preventDefault()

      if (isExpandable(item)) {
        toggleExpand(index)
        return
      }

      if (isNavigable(item)) {
        navigateTo(item)
      }

      if (isOverlay(item)) {
        props.onOverlayMenuItemClick(event, item)
      }
    }

    /** @param {number} index */
    function toggleExpand(index) {
      setActiveIndex(index === activeIndex ? null : index)
    }

    /** @param {RenderableNavigableAppMenuItem} item */
    function navigateTo(item) {
      navigate(item.path)
      setKeyPath(item.path)
      setScreenTitle(item.name)
      setSidebarActive(false)

      item.path !== "/" ? setShowFiltersMenu(true) : setHideMobileMenu(true)
    }

    /** @param {number} index */
    function onMenuItemMouseEnter(index) {
      if (props.root && props.menuActive && isHorizontalOrSlim()) {
        setActiveIndex(index)
      }
    }
    function isStatic() {
      return props.menuMode === "static"
    }

    const isHorizontalOrSlim = useCallback(() => {
      return props.menuMode === "horizontal" || props.menuMode === "slim"
    }, [props.menuMode])

    /** @param {RenderableAppMenuItem} item */
    const renderIcon = (item) => {
      let menuitemIconClassName = null

      if (item?.iconClass) {
        menuitemIconClassName = classNames(
          "layout-menuitem-icon",
          item?.iconClass
        )
      }

      if (menuitemIconClassName) {
        return <i className={menuitemIconClassName}></i>
      }

      return <IconMaterial fontSize="medium" icon={item?.icon} color="gray" />
    }

    /**
     * @param {RenderableAppMenuItem} item
     * @param {number} index
     * @returns {JSX.Element}
     */
    function getLink(item, index) {
      const content = (
        <>
          {renderIcon(item)}
          <span className="layout-menuitem-text">{item.name}</span>
          {isExpandable(item) && (
            <i className="pi pi-fw pi-chevron-down  layout-submenu-toggler" />
          )}
          {isOverlay(item) && (
            <i className="pi pi-fw pi-chevron-right layout-submenu-toggler" />
          )}
          <Ripple />
        </>
      )
      const commonLinkProps = {
        className: classNames("p-ripple", {
          "active-route": item.path === keyPath,
        }),
        /** @param {React.MouseEvent} e */
        onClick: (e) => onMenuItemClick(e, item, index),
        onMouseEnter: () => onMenuItemMouseEnter(index),
      }

      if (isNavigable(item)) {
        return (
          <BLink
            to={item.path}
            href={item.path}
            rel="noopener noreferrer"
            {...commonLinkProps}
          >
            {content}
          </BLink>
        )
      }

      return <a {...commonLinkProps}>{content}</a>
    }

    function getItems() {
      const transitionTimeout =
        isHorizontalOrSlim() && !props.root ? { enter: 1000, exit: 450 }
        : isHorizontalOrSlim() ? 0
        : { enter: 1000, exit: 450 }
      return props.items.map((item, i) => {
        const submenuRef = createRef()

        const active = activeIndex === i
        const menuitemClassName = classNames({
          "layout-root-menuitem": props.root,
          "active-menuitem": active,
        })
        const link = getLink(item, i)
        return (
          <li
            key={item.name || i}
            className={menuitemClassName}
            role="menuitem"
          >
            {props.root && isStatic() && (
              <div className="layout-menuitem-text">{item.name}</div>
            )}
            {link}
            {isExpandable(item) && (
              <CSSTransition
                nodeRef={submenuRef}
                classNames="p-toggleable-content"
                timeout={transitionTimeout}
                in={(isExpandable(item) && props.root && isStatic()) || active}
                unmountOnExit
              >
                <AppSubmenu
                  ref={submenuRef}
                  items={item.modules}
                  menuActive={props.menuActive}
                  menuMode={props.menuMode}
                  parentMenuItemActive={active}
                  onOverlayMenuItemClick={props.onOverlayMenuItemClick}
                />
              </CSSTransition>
            )}
          </li>
        )
      })
    }

    useEffect(() => {
      if (props.resetActiveIndex && isHorizontalOrSlim()) {
        setActiveIndex(null)
      }
    }, [props.resetActiveIndex, isHorizontalOrSlim])

    useEffect(() => {
      if (!props.menuActive && isHorizontalOrSlim()) {
        setActiveIndex(null)
      }
    }, [props.menuActive, isHorizontalOrSlim])

    if (!props.items) {
      return null
    }
    const items = getItems()
    return (
      <ul ref={ref} className={props.className} role="menu">
        {items}
      </ul>
    )
  }
)
